import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Paper,
  CircularProgress,
  Divider,
  styled,
  Avatar,
  Chip,
  Fade,
  Slide,
  IconButton,
  Grow,
} from '@mui/material'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faArrowRight,
  faArrowLeft,
  faCreditCard,
  faShieldAlt,
  faUser,
  faClock,
  faTag,
  faCheckCircle,
  faGift,
} from '@fortawesome/free-solid-svg-icons'
import lightTheme from '../../../../styles/theme/lightTheme'
import StripeSubscriptionTest from '../../../../components/Stripe/StripeSubscriptionTest'
import StripeAppTest from '../../../../components/Stripe/StripeAppTest'
import Api from '../../../../services/api.service'
import { handlingErrors } from '../../../../utils'
import {
  faCircleCheck,
  faCircleExclamation,
} from '@fortawesome/pro-solid-svg-icons'
import { updateUser } from '../../../../store/slices/userAuthReducer'

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #2196F3 0%, #21CBF3 100%)',
  },
  '&:hover': {
    boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 0 20px rgba(33, 150, 243, 0.1)',
    '&::before': {
      background: 'linear-gradient(90deg, #21CBF3 0%, #2196F3 100%)',
    },
  },
}))

const PlanDetailsCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: { xs: 0, md: theme.spacing(3) },
  borderRadius: '16px',
  border: '2px solid transparent',
  background:
    'linear-gradient(white, white) padding-box, linear-gradient(135deg, #667eea 0%, #764ba2 100%) border-box',
  backgroundColor: theme.palette.background.paper,
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '200%',
    height: '200%',
    background:
      'radial-gradient(circle, rgba(33, 150, 243, 0.05) 0%, transparent 70%)',
    animation: 'pulse 4s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%': { transform: 'scale(0.8)', opacity: 0.5 },
    '50%': { transform: 'scale(1.1)', opacity: 0.8 },
    '100%': { transform: 'scale(0.8)', opacity: 0.5 },
  },
}))

const StyledStepper = styled(Stepper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& .MuiStepIcon-root': {
    color: theme.palette.grey[300],
    fontSize: '32px',
  },
  '& .MuiStepIcon-text': {
    fill: theme.palette.common.white,
    fontSize: '16px',
    fontWeight: 'bold',
  },
  '& .Mui-active .MuiStepIcon-root': {
    color: theme.palette.primary.main,
    boxShadow: '0 0 0 4px rgba(33, 150, 243, 0.2)',
  },
  '& .Mui-completed .MuiStepIcon-root': {
    color: theme.palette.success.main,
  },
  '& .MuiStepLabel-root': {
    fontSize: '14px',
    fontWeight: 500,
  },
  '& .MuiStepLabel-label': {
    fontSize: '14px',
    fontWeight: 500,
    '&.Mui-active': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    '&.Mui-completed': {
      color: theme.palette.success.main,
    },
  },
}))

const PriceDisplay = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: theme.spacing(3),
  borderRadius: '16px',
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background:
      'linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
  },
  '@keyframes shimmer': {
    '0%': { transform: 'translateX(-100%) translateY(-100%)' },
    '100%': { transform: 'translateX(100%) translateY(100%)' },
  },
}))

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  borderRadius: '12px',
  marginBottom: theme.spacing(1.5),
  background:
    'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(129, 199, 132, 0.1) 100%)',
  border: '1px solid rgba(76, 175, 80, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background:
      'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(129, 199, 132, 0.2) 100%)',
    transform: 'translateX(8px)',
  },
}))

const BackButton = styled(IconButton)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  background: 'rgba(33, 150, 243, 0.1)',
  color: theme.palette.primary.main,
  '&:hover': {
    background: 'rgba(33, 150, 243, 0.2)',
    transform: 'translateX(-4px)',
  },
}))

export default function SubscriptionPlanDetails() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const userData = useSelector((state) => state.userAuth.user)
  const dispatch = useDispatch()
  const [offer, setOffer] = useState(null)
  const [loading, setLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [paymentMethod, setPaymentMethod] = useState('one_shot')
  const [period, setPeriod] = useState('3')
  const [cardInfo, setCardInfo] = useState({
    number: '',
    expiry: '',
    cvc: '',
    name: '',
    email: '',
  })

  // Load offer from sessionStorage
  useEffect(() => {
    const storedOffer = sessionStorage.getItem('offerSelected')
    const storedPeriod = sessionStorage.getItem('periodeSelected')
    const storedPaymentType = sessionStorage.getItem('paymentType')

    if (storedOffer) {
      try {
        const parsedOffer = JSON.parse(storedOffer)
        setOffer(parsedOffer)

        if (storedPaymentType) {
          setPaymentMethod(storedPaymentType)
        }

        if (storedPeriod) {
          setPeriod(storedPeriod)
        }
      } catch (error) {
        console.error('Error parsing offer:', error)
        // Handle error - maybe redirect to offers page
        navigate('/entreprises/offres')
      }
    }
  }, [])

  const handleCardInfoChange = (field) => (event) => {
    setCardInfo({
      ...cardInfo,
      [field]: event.target.value,
    })
  }

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method)
  }

  const validateCardInfo = () => {
    const errors = {}

    if (!cardInfo.number.trim()) {
      errors.number = t('payment.cardNumberRequired')
    }

    if (!cardInfo.expiry.trim()) {
      errors.expiry = t('payment.expiryRequired')
    }

    if (!cardInfo.cvc.trim()) {
      errors.cvc = t('payment.cvcRequired')
    }

    if (!cardInfo.name.trim()) {
      errors.name = t('payment.nameRequired')
    }

    if (!cardInfo.email.trim()) {
      errors.email = t('payment.emailRequired')
    }

    return Object.keys(errors).length === 0
  }

  const handlePaymentSuccess = () => {
    setCurrentStep(2)
    setLoading(false)

    getCompanySubscriptions()

    // Store payment info for receipt
    sessionStorage.setItem(
      'paymentInfo',
      JSON.stringify({
        method: 'card',
        last4: cardInfo.number.slice(-4),
        amount:
          paymentMethod === 'one_shot'
            ? offer?.price
            : paymentMethod === 'monthly'
            ? offer?.monthlyPrice * offer?.quantity
            : offer?.annualPrice * offer?.quantity,
        currency:
          JSON.parse(sessionStorage.getItem('currency')).devise || 'CHF',
      })
    )
    navigate('/user')
  }

  const handlePaymentClose = () => {
    setLoading(false)
  }

  const renderPlanDetails = () => {
    if (!offer) {
      return (
        <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            {t('payment.noOfferSelected')}
          </Typography>
        </Container>
      )
    }
  }

  const activateFreePlan = async () => {
    setLoading(true)

    try {
      const result = await Api.companySubscriptions.activateFreePlan({
        utilisateur: userData.data.id,
      })
      const res = handlingErrors(result)

      if (res.status === 500) {
        toast.error(t('successMessage.somethingWrong'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
        //setMessage(t('successMessage.paymentNok'))
        setLoading(false)
        return
      }

      if (res.ok && res.data.code === 500) {
        toast.error(t('successMessage.acitvateFreePlanError'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleExclamation}
              fontSize={25}
              color={lightTheme.palette.error.main}
            />
          ),
        })
        //setMessage(t('successMessage.paymentNok'))
        setLoading(false)
        setTimeout(() => {
          sessionStorage.removeItem('offerSelected')
          sessionStorage.removeItem('periodeSelected')
          sessionStorage.removeItem('paymentType')
          navigate('/entreprises/offres')
        }, 1000)
        return
      }

      if (res.ok) {
        toast.success(t('successMessage.acitvateFreePlanOk'), {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          icon: ({ theme, type }) => (
            <FontAwesomeIcon
              icon={faCircleCheck}
              fontSize={25}
              color={lightTheme.palette.success.main}
            />
          ),
        })

        getCompanySubscriptions()
        //setMessage(t('successMessage.paymentOk'))
        setLoading(false)
        setTimeout(() => {
          sessionStorage.removeItem('offerSelected')
          sessionStorage.removeItem('periodeSelected')
          sessionStorage.removeItem('paymentType')
          navigate('/user')
        }, 1000)
      }
    } catch (error) {
      console.log(error)
      setLoading(false)
    }
  }

  const getCompanySubscriptions = async () => {
    try {
      let result = await Api.companySubscriptions.getCompanySubscriptions({
        utilisateur: `/api/utilisateurs/${userData?.data.id}`,
      })

      const handling = handlingErrors(result)

      if (handling.ok) {
        console.log(result.data)
        dispatch(
          updateUser({
            data: {
              offres: result.data.data.offres,
              allowedPermissions: result.data.data.allowedPermissions,
              autorise: result.data.data.autorise,
            },
          })
        )
      } else {
        console.log(handling)
      }
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 6, minHeight: '100vh' }}>
      <Fade in={true} timeout={600}>
        <Box>
          <BackButton onClick={() => navigate('/entreprises/offres')}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </BackButton>

          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'center',
              mb: 2,
            }}
          >
            {t('payment.selectedPlan')}
          </Typography>

          <Typography variant="h6" sx={{ textAlign: 'center', mb: 6 }}>
            {t('payment.choosePerfectPlan')}
          </Typography>

          {/*  <StyledStepper activeStep={currentStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={index}>
                <StepLabel icon={step.icon}>{step.label}</StepLabel>
              </Step>
            ))}
          </StyledStepper> */}

          <Grid container spacing={4}>
            <Grid item xs={12} lg={8}>
              <Slide direction="left" in={true} timeout={1000}>
                <StyledCard>
                  <CardContent
                    sx={{
                      p: { xs: 0, md: 4 },
                      pb: { xs: '0px !important', md: '32px !important' },
                    }}
                  >
                    {!offer ? (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="error" gutterBottom>
                          {t('payment.noOfferSelected')}
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={() => navigate('/entreprises/offres')}
                          sx={{ mt: 2 }}
                        >
                          {t('payment.browseOffers')}
                        </Button>
                      </Box>
                    ) : (
                      <PlanDetailsCard>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 4,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: 'primary.main',
                              width: 56,
                              height: 56,
                              mr: 2,
                            }}
                          >
                            <FontAwesomeIcon icon={faTag} />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h4"
                              gutterBottom
                              sx={{ fontWeight: 'bold' }}
                            >
                              {offer?.name}
                            </Typography>

                            {offer?.duration && (
                              <Typography variant="body2" sx={{ mb: 2 }}>
                                {t('payment.duration')}: {offer?.duration}
                                {i18n.language === 'fr' ? 'j' : 'd'}
                              </Typography>
                            )}
                            <Chip
                              label={
                                paymentMethod === 'one_shot' ||
                                paymentMethod === 'trial'
                                  ? t('payment.oneTime')
                                  : t('payment.monthly')
                              }
                              variant="outlined"
                              size="small"
                            />
                          </Box>
                        </Box>
                        {/* <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <Box sx={{ mb: 3 }}>
                                  <Typography
                                    variant="body2"
                                    gutterBottom
                                  >
                                    {t('payment.planType')}
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {paymentMethod === 'one_shot'
                                      ? t('payment.oneTime')
                                      : paymentMethod === 'monthly'
                                      ? t('payment.monthly')
                                      : t('payment.yearly')}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Box sx={{ mb: 3 }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    gutterBottom
                                  >
                                    {t('payment.duration')}
                                  </Typography>
                                  <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {offer?.type === 'one_shot'
                                      ? `${offer?.periodDays} ${t(
                                          'payment.days'
                                        )}`
                                      : paymentMethod === 'one_shot'
                                      ? t('payment.oneTime')
                                      : paymentMethod === 'monthly'
                                      ? t('payment.monthly')
                                      : t('payment.yearly')}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid> */}

                        <Divider sx={{ my: 3 }} />

                        <PriceDisplay>
                          {paymentMethod !== 'trial' && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1, opacity: 0.9 }}
                            >
                              {t('payment.price')}
                            </Typography>
                          )}
                          <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                            {paymentMethod !== 'trial' && (
                              <>
                                {JSON.parse(sessionStorage.getItem('currency'))
                                  .devise || 'CHF'}{' '}
                              </>
                            )}
                            {paymentMethod === 'trial'
                              ? t('payment.free')
                              : paymentMethod === 'one_shot'
                              ? (() => {
                                  switch (period) {
                                    case '3':
                                      return `${offer?.threeMonthsPrice}`
                                    case '6':
                                      return `${offer?.sixMonthsPrice}`
                                    case '12':
                                      return `${offer?.twelveMonthsPrice}`
                                    default:
                                      return `${offer?.price}`
                                  }
                                })()
                              : `${offer?.monthlyPrice}`}
                          </Typography>
                          {/* <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ opacity: 0.8 }}
                              >
                                {t('payment.quantity')}: {offer?.quantity}
                              </Typography> */}
                        </PriceDisplay>

                        <Box sx={{ mt: 4 }}>
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                          >
                            {t('payment.features')}
                          </Typography>
                          {offer?.features?.map((feature, index) => (
                            <FeatureItem key={index}>
                              <FontAwesomeIcon
                                icon={faCheckCircle}
                                style={{
                                  color: '#4caf50',
                                  marginRight: '16px',
                                  fontSize: '20px',
                                }}
                              />
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500 }}
                              >
                                {feature}
                              </Typography>
                            </FeatureItem>
                          ))}
                        </Box>
                        {offer?.ideal && (
                          <Box
                            mb={{ xs: 3, sm: 4 }}
                            p={{ xs: 2, sm: 3 }}
                            sx={{
                              background:
                                'linear-gradient(135deg, rgba(25, 118, 210, 0.08), rgba(66, 165, 245, 0.12))',
                              borderRadius: '16px',
                              border: '1px solid rgba(25, 118, 210, 0.15)',
                            }}
                          >
                            <Typography
                              variant="body1"
                              fontWeight="700"
                              color="primary.main"
                              gutterBottom
                              sx={{
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                              }}
                            >
                              {t('subscriptions.idealFor')}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                lineHeight: 1.5,
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                              }}
                            >
                              {offer?.ideal}
                            </Typography>
                          </Box>
                        )}
                        {offer?.description && (
                          <Box
                            mb={{ xs: 3, sm: 4 }}
                            p={{ xs: 2, sm: 3 }}
                            sx={{
                              background:
                                'linear-gradient(135deg, rgba(25, 118, 210, 0.08), rgba(66, 165, 245, 0.12))',
                              borderRadius: '16px',
                              border: '1px solid rgba(25, 118, 210, 0.15)',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                lineHeight: 1.5,
                                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                              }}
                            >
                              {offer?.description}
                            </Typography>
                          </Box>
                        )}
                      </PlanDetailsCard>
                    )}
                  </CardContent>
                </StyledCard>
              </Slide>
            </Grid>

            {paymentMethod === 'trial' ? (
              <Grid item xs={12} lg={4}>
                <Slide direction="left" in={true} timeout={1000}>
                  <Box>
                    <StyledCard
                      sx={{
                        background:
                          'linear-gradient(135deg, rgba(191, 79, 206, 0.05) 0%, rgba(159, 64, 172, 0.05) 100%)',
                        border: '2px solid rgba(191, 79, 206, 0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          border: '2px solid rgba(191, 79, 206, 0.3)',
                          boxShadow: '0 12px 32px rgba(191, 79, 206, 0.15)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 4 }}
                        >
                          <Avatar
                            sx={{
                              background:
                                'linear-gradient(135deg, #BF4FCE 0%, #9F40AC 100%)',
                              width: 56,
                              height: 56,
                              mr: 2.5,
                              boxShadow: '0 4px 12px rgba(191, 79, 206, 0.3)',
                              fontSize: '24px',
                            }}
                          >
                            <FontAwesomeIcon icon={faGift} />
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: '700', mb: 0.5 }}
                            >
                              {t('payment.freePlan')}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 500 }}
                            >
                              {t('payment.getStartedForFree')}
                            </Typography>
                          </Box>
                        </Box>

                        <Grow in={true} timeout={1200}>
                          <Box>
                            <Box
                              sx={{
                                background: 'rgba(255, 255, 255, 0.6)',
                                border: '1px solid rgba(191, 79, 206, 0.1)',
                                borderRadius: '8px',
                                p: 2.5,
                                mb: 3,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 0,
                                  color: 'text.primary',
                                  fontWeight: 500,
                                  lineHeight: 1.6,
                                }}
                              >
                                {t('payment.freePlanDescription')}
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={activateFreePlan}
                              sx={{
                                background:
                                  'linear-gradient(135deg, #BF4FCE 0%, #9F40AC 100%)',
                                color: '#ffffff',
                                padding: '14px 24px',
                                fontSize: '16px',
                                fontWeight: '700',
                                textTransform: 'none',
                                borderRadius: '10px',
                                boxShadow: '0 4px 16px rgba(191, 79, 206, 0.4)',
                                transition: 'all 0.3s ease',
                                border: 'none',
                                '&:hover': {
                                  boxShadow:
                                    '0 8px 32px rgba(191, 79, 206, 0.5)',
                                  transform: 'translateY(-3px)',
                                  background:
                                    'linear-gradient(135deg, #9F40AC 0%, #7D318A 100%)',
                                },
                                '&:active': {
                                  transform: 'translateY(-1px)',
                                },
                              }}
                            >
                              {loading ? (
                                <CircularProgress
                                  size={28}
                                  sx={{ color: 'white' }}
                                />
                              ) : (
                                t('button.activateFree')
                              )}
                            </Button>
                          </Box>
                        </Grow>
                      </CardContent>
                    </StyledCard>
                  </Box>
                </Slide>
              </Grid>
            ) : (
              <Grid item xs={12} lg={4}>
                <Slide direction="left" in={true} timeout={1000}>
                  <Box>
                    <StyledCard>
                      <CardContent sx={{ p: 4 }}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: 'secondary.main',
                              width: 48,
                              height: 48,
                              mr: 2,
                            }}
                          >
                            <FontAwesomeIcon icon={faCreditCard} />
                          </Avatar>
                          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            {t('payment.paymentMethod')}
                          </Typography>
                        </Box>

                        {offer && userData ? (
                          <Grow in={true} timeout={1200}>
                            <Box>
                              <Typography variant="body2" sx={{ mb: 3 }}>
                                {t('payment.securePaymentStripe')}
                              </Typography>
                              {paymentMethod === 'one_shot' ? (
                                <StripeAppTest
                                  userId={userData.data.id}
                                  offreId={offer?.id}
                                  offrePeriode={parseInt(period)}
                                  type={paymentMethod}
                                  offrePrice={
                                    paymentMethod === 'one_shot'
                                      ? (() => {
                                          switch (period) {
                                            case '3':
                                              return offer?.threeMonthsPrice
                                            case '6':
                                              return offer?.sixMonthsPrice
                                            case '12':
                                              return offer?.twelveMonthsPrice
                                            default:
                                              return offer?.price
                                          }
                                        })()
                                      : offer?.monthlyPrice
                                  }
                                  onSuccess={handlePaymentSuccess}
                                  onClose={handlePaymentClose}
                                  codePromoObject={{}}
                                  offreQuantity={offer?.quantity}
                                />
                              ) : (
                                <StripeSubscriptionTest
                                  userId={userData.data.id}
                                  offreId={offer?.id}
                                  offrePeriode={parseInt(period)}
                                  type={paymentMethod}
                                  offrePrice={offer?.monthlyPrice}
                                  onSuccess={handlePaymentSuccess}
                                  onClose={handlePaymentClose}
                                  codePromoObject={{}}
                                  offreQuantity={offer?.quantity}
                                />
                              )}
                            </Box>
                          </Grow>
                        ) : (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <FontAwesomeIcon
                              icon={faCreditCard}
                              style={{
                                fontSize: '48px',
                                color: 'rgba(0, 0, 0, 0.6)',
                                marginBottom: '16px',
                              }}
                            />
                            <Typography variant="body2">
                              {t('payment.pleaseSelectOffer')}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </StyledCard>

                    <Box sx={{ mt: 3 }}>
                      <Paper
                        sx={{
                          p: 3,
                          background:
                            'linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(33, 150, 243, 0.05) 100%)',
                          border: '1px solid rgba(33, 150, 243, 0.2)',
                          borderRadius: '16px',
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                        >
                          <FontAwesomeIcon
                            icon={faShieldAlt}
                            style={{
                              color: '#2196F3',
                              marginRight: '8px',
                            }}
                          />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t('payment.securePayment')}
                          </Typography>
                        </Box>
                        <Typography variant="body2">
                          {t('payment.paymentInfoEncrypted')}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                </Slide>
              </Grid>
            )}
          </Grid>
        </Box>
      </Fade>
    </Container>
  )
}
